import { createStore, applyMiddleware, compose } from 'redux';
import type { Middleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { Product, AuthUtils } from '@portal/portal-core';

import { AdminClientMiddlewares, AdminClientActions } from 'ducks/admin-client';
import { DeepLinkMiddleware, shouldUpdateQueryParams } from 'middlewares';
import { AuthClientActions, AuthClientUtils } from 'ducks/auth-client';
import { UserClientActions } from 'ducks/user-client';
import { pollInterval, serverUrl } from '../Config';
import { ProductTabActions } from 'actions';
import Reducers from 'reducers';
import rootSaga from 'sagas';

import {
  isSuperAdmin,
  getClientStorageValue,
  setClientStorageValue,
  navigate as navigateTo,
} from 'utils';
import { AuthClientMiddleware, vanillaPromise } from '../middlewares/cloud-middlewares';

const config = {
  serverUrl,
  pollInterval,
  launchAuth: AuthClientActions.signInUnauthorized,
  hideAuth: AuthClientActions.signInSuccess,
  getAuthTokenKey: AuthClientUtils.getAuthTokenKey,
  getAuthJwtToken: AuthClientUtils.getAuthJwtToken,
  setAuthJwtToken: AuthClientUtils.setAuthJwtToken,
  getUserLastActiveTimestamp: AuthClientUtils.getUserLastActiveTimestamp,
  getClientStorageValue,
  setClientStorageValue,
  userSyncPollCycle: AuthClientUtils.userSyncPollCycle,
  getClientToApiTimestampDiff: AuthClientUtils.clientToApiTimestampDiff,
  navigateTo,
  getProductName: (): string => Product.NAMES.cloud,
  setSelectedProduct: ProductTabActions.selectedProductTab,
  isSuperAdmin,
  syncUserInvitesAction: UserClientActions.clientInvitesSync,
  syncAdminInvitesAction: AdminClientActions.clientProductAccessInvitesSync,
  setAuthenticatedTrue: AuthClientActions.signInSuccess,
  getCurrentUserIdFromJwt: AuthUtils.getUserIdFromJWT,
  signOut: AuthClientActions.signOut,
};

// The Redux DevTools extension augments `window` at runtime; it is not part of
// the app's ambient type surface, so narrow it locally at this boundary.
const composeEnhancers =
  (window as Window & typeof globalThis & { __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose })
    .__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const sagaMiddleware = createSagaMiddleware();

export const configureStore = () => {
  const store = createStore(
    Reducers,
    {},
    composeEnhancers(
      applyMiddleware(
        // These app middlewares are authored against local structural boundary types
        // (untyped store/action) rather than redux's `Middleware<unknown, any, any>`, so
        // cast them at this wiring boundary. Runtime behavior is unchanged.
        AuthClientMiddleware(config) as Middleware,
        vanillaPromise as Middleware,
        sagaMiddleware,
        AdminClientMiddlewares.DeepLinkMiddleware({
          shouldUpdateQueryParams,
        }) as Middleware,
        DeepLinkMiddleware as Middleware,
      ),
    ),
  );
  sagaMiddleware.run(rootSaga);
  return store;
};

export type AppDispatch = ReturnType<typeof configureStore>['dispatch'];
